import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { Mail, ShieldCheck, HelpCircle, XCircle, CheckCircle2, ExternalLink } from "lucide-react";

const TILES = [
  { key: "awaiting_my_review", label: "Awaiting My Review", icon: Mail, accent: "text-orange-700 bg-orange-50 border-orange-200" },
  { key: "recent_reviewed", label: "Recently Reviewed", icon: ShieldCheck, accent: "text-teal-700 bg-sky-50 border-sky-200" },
  { key: "more_info_requested", label: "More Information Requested", icon: HelpCircle, accent: "text-purple-700 bg-purple-50 border-purple-200" },
  { key: "rejected", label: "Rejected", icon: XCircle, accent: "text-red-700 bg-red-50 border-red-200" },
  { key: "approved_released", label: "Approved / Released", icon: CheckCircle2, accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
];

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/dashboard/client").then(r => setData(r.data)).catch(e => setErr(e.message));
  }, []);

  if (err) return <div className="p-8 text-red-700">Failed to load: {err}</div>;
  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 md:p-8" data-testid="client-dashboard">
      <div className="mb-6">
        <div className="overline mb-1">Reviewer dashboard</div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">My Hold Point Reviews</h1>
        <p className="mt-1 text-sm text-slate-600">Hold-point release requests assigned to you. Click a row to open the secure review.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {TILES.map(t => (
          <Card key={t.key} className="border-slate-200 p-4" data-testid={`client-tile-${t.key}`}>
            <div className={`grid size-9 place-items-center rounded-md border ${t.accent}`}><t.icon className="size-4"/></div>
            <div className="mt-3 font-heading text-4xl font-black tracking-tighter">{data.summary[t.key] ?? 0}</div>
            <div className="text-xs font-medium text-slate-600">{t.label}</div>
          </Card>
        ))}
      </div>

      <h2 className="mt-8 font-heading text-xl font-bold tracking-tight">Hold Points</h2>
      <Card className="mt-3 overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-4 py-3">Project</th><th className="px-4 py-3">Lot</th>
                <th className="px-4 py-3">HP</th><th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Location</th><th className="px-4 py-3">Submitted By</th>
                <th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No hold point reviews assigned to you yet.</td></tr>}
              {data.items.map(it => (
                <tr key={it.token} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`client-review-row-${it.lot_number}-${it.hp_number}`}>
                  <td className="px-4 py-3 text-slate-700">{it.project_name}</td>
                  <td className="px-4 py-3 font-mono font-semibold">{it.lot_number}</td>
                  <td className="px-4 py-3 font-mono text-teal-700">{it.hp_number}</td>
                  <td className="px-4 py-3">{it.hp_title}</td>
                  <td className="px-4 py-3 text-slate-600">{it.location || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{it.submitted_by_name}</td>
                  <td className="px-4 py-3 text-slate-500">{it.submitted_at && new Date(it.submitted_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><StatusBadge status={it.status}/></td>
                  <td className="px-4 py-3">
                    <a href={`/client-review/${it.token}`} target="_blank" rel="noreferrer" data-testid={`client-review-link-${it.hp_number}`}>
                      <Button size="sm" className="bg-teal-700 hover:bg-teal-800">Review <ExternalLink className="ml-1 size-3.5"/></Button>
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
